import type { HttpRequest } from '@nauth-toolkit/client';
import { authHttpAdapter, authorizationHeader } from './auth';
import { API_BASE_URL } from './env';

/**
 * Rundown's own endpoints. Authentication is entirely the nauth SDK's: the
 * bearer comes from its token store, and a 401 is refreshed and retried by the
 * same adapter the SDK uses for its own calls. Every failure is a
 * `NAuthClientError` — `statusCode` for HTTP errors, `isNetworkError` otherwise.
 */

export interface RequestOptions {
  readonly method?: HttpRequest['method'];
  /** Serialised as JSON. Use `undefined`, not `null`, for "no body". */
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await authHttpAdapter.request<T>({
    method: options.method ?? 'GET',
    url: `${API_BASE_URL}${path}`,
    headers: {
      Accept: 'application/json',
      ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(await authorizationHeader()),
    },
    body: options.body,
    signal: options.signal,
    credentials: 'omit',
  });
  return response.data;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal): Promise<T> => apiFetch<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
    apiFetch<T>(path, { method: 'POST', body, signal }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
    apiFetch<T>(path, { method: 'PATCH', body, signal }),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
    apiFetch<T>(path, { method: 'PUT', body, signal }),
  delete: <T>(path: string, signal?: AbortSignal): Promise<T> =>
    apiFetch<T>(path, { method: 'DELETE', signal }),
} as const;
